import {
  PwaUpdatePrompt,
  useGlobalError,
} from '@brunommsilva/react-foundations';
import { Box, CircularProgress, Container, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';

import AssistantWidget from '../components/assistant/assistant-widget';
import Protected from '../components/auth/protected';
import NavigationBar from '../components/navigation-bar/navigation-bar';
import ThemeProvider from '../components/theme/theme-provider';
import AppConfig from '../config/app-config';
import * as PATHS from '../config/paths';
import AccountsPage from '../pages/accounts-page';
import AssistantPage from '../pages/assistant-page';
import CategoriesPage from '../pages/categories-page';
import DashboardPage from '../pages/dashboard-page';
import ErrorBoundaryPage from '../pages/error-boundary-page';
import GlobalErrorPage from '../pages/global-error-page';
import SignInPage from '../pages/signin-page';
import SignOutPage from '../pages/signout-page';
import TransactionsPage from '../pages/transactions-page';
import UnauthorizedPage from '../pages/unauthorized-page';

export default function App() {
  const { t } = useTranslation();
  const { inError, errorType, retry } = useGlobalError();

  const loader = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('LOADING')}
      </Typography>
    </Box>
  );

  // Off by default: nothing is rendered, and the assistant does not even ask the backend for a
  // conversation. On a phone it is a page of its own, elsewhere a floating window.
  const renderAssistantWidget = () =>
    AppConfig.isAssistantEnabled() ? <AssistantWidget /> : <></>;

  const renderWithNavigationBar = (element: ReactElement) => (
    <Protected loader={loader}>
      <Box
        sx={{
          // The assistant, when docked at the side, sets how much room it takes, from the top bar down.
          pr: 'var(--assistant-dock, 0px)',
          transition: 'padding-right 0.2s',
        }}
      >
        <NavigationBar />
        <Container maxWidth="lg">
          <Box sx={{ my: 3, pb: { xs: 16, md: 0 } }}>{element}</Box>
        </Container>
      </Box>
      {renderAssistantWidget()}
    </Protected>
  );

  const renderAssistantPage = () => (
    <Protected loader={loader}>
      <AssistantPage />
    </Protected>
  );

  const basePath = AppConfig.getBasePath();

  const router = createBrowserRouter(
    [
      {
        errorElement: <ErrorBoundaryPage />,
        children: [
          {
            path: PATHS.DASHBOARD_PATH,
            element: renderWithNavigationBar(<DashboardPage />),
          },
          {
            path: PATHS.ACCOUNTS_PATH,
            element: renderWithNavigationBar(<AccountsPage />),
          },
          {
            path: PATHS.TRANSACTIONS_PATH,
            element: renderWithNavigationBar(<TransactionsPage />),
          },
          {
            path: PATHS.CATEGORIES_PATH,
            element: renderWithNavigationBar(<CategoriesPage />),
          },
          ...(AppConfig.isAssistantEnabled()
            ? [
                {
                  path: PATHS.ASSISTANT_PATH,
                  element: renderAssistantPage(),
                },
              ]
            : []),
          {
            path: PATHS.LOGIN_PATH,
            element: <SignInPage />,
          },
          {
            path: PATHS.LOGOUT_PATH,
            element: <SignOutPage />,
          },
          {
            path: PATHS.UNAUTHORIZED_PATH,
            element: <UnauthorizedPage />,
          },
          {
            path: '*',
            element: <Navigate to={PATHS.DASHBOARD_PATH} replace />,
          },
        ],
      },
    ],
    {
      basename: basePath,
    }
  );

  return (
    <ThemeProvider>
      {inError ? (
        <GlobalErrorPage errorType={errorType} onRetry={retry} />
      ) : (
        <RouterProvider router={router} />
      )}
      <PwaUpdatePrompt />
    </ThemeProvider>
  );
}
